import { Link } from 'react-router-dom';
import PublicLayout, { SEOHead, CTASection } from '../../components/PublicLayout';
import { US_CITIES, UK_CITIES, CA_CITIES, AU_CITIES, US_STATES, UK_REGIONS, CA_PROVINCES, AU_STATES, toSlug } from '../../data/seo-data';
import IMAGES from '../../data/seo-images';

function CityGrid({ title, cities, country }) {
  return (
    <section className="mb-12">
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <p className="text-sm text-slate-500 mb-4">{country}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {cities.map(c => (
          <Link key={c} to={`/interpreting-services-${toSlug(c)}`} className="px-3 py-2 border rounded-lg text-sm hover:bg-blue-50 hover:border-blue-200 transition-colors">
            {c}
          </Link>
        ))}
      </div>
    </section>
  );
}

function RegionGrid({ title, regions }) {
  return (
    <section className="mb-12">
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {regions.map(r => (
          <Link key={r} to={`/interpreting-services-${toSlug(r)}`} className="px-3 py-2 border rounded-lg text-sm hover:bg-blue-50 hover:border-blue-200 transition-colors">
            {r}
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function LocationsHubPage() {
  return (
    <PublicLayout>
      <SEOHead title="Interpreter Services by Location | US Cities & States | Interpreter Services" description="Find professional interpreter services in your city. We serve the top 100 US cities, 20 UK cities, 20 Canadian cities, and 20 Australian cities with phone, video, and on-site interpreters." canonical="https://interpreterservices.us/locations" />

      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Interpreter Services by Location</h1>
            <p className="text-lg text-blue-100 max-w-3xl">Find professional interpreter services near you. We provide phone, video, and on-site interpreting across the United States, Canada, the United Kingdom, and Australia.</p>
          </div>
          <div className="hidden lg:block">
            <img src={IMAGES.globe[0].url} alt={IMAGES.globe[0].alt} className="rounded-2xl shadow-lg w-full object-cover h-56" loading="eager" data-testid="lochub-img-1" />
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <img src={IMAGES.corporate[0].url} alt={IMAGES.corporate[0].alt} className="w-full rounded-xl mb-10 object-cover h-56 md:h-72" loading="lazy" data-testid="lochub-img-2" />
        <CityGrid title="United States — Top Cities" cities={US_CITIES} country="United States" />
        <RegionGrid title="US States & Territories" regions={US_STATES} />
        <CityGrid title="United Kingdom — Cities" cities={UK_CITIES} country="United Kingdom" />
        <RegionGrid title="UK Regions" regions={UK_REGIONS} />
        <CityGrid title="Canada — Cities" cities={CA_CITIES} country="Canada" />
        <RegionGrid title="Canadian Provinces" regions={CA_PROVINCES} />
        <img src={IMAGES.onsite[1].url} alt={IMAGES.onsite[1].alt} className="w-full rounded-xl mb-10 object-cover h-48 md:h-64" loading="lazy" data-testid="lochub-img-3" />
        <CityGrid title="Australia — Cities" cities={AU_CITIES} country="Australia" />
        <RegionGrid title="Australian States & Territories" regions={AU_STATES} />
        <img src={IMAGES.teamwork[2].url} alt={IMAGES.teamwork[2].alt} className="w-full rounded-xl mb-4 object-cover h-48 md:h-64" loading="lazy" data-testid="lochub-img-4" />
      </div>

      <CTASection variant="secondary" />
    </PublicLayout>
  );
}
